import React, { useState } from 'react';

const Hero = () => {
  const [expandedImage, setExpandedImage] = useState(null);

  const scrollToWhatWeDo = () => {
    const element = document.getElementById('what-we-do');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-[85vh] flex items-center border-b border-gray-200 overflow-hidden">
      <style>{`
        @keyframes heroLineRise {
          from {
            opacity: 0;
            transform: translateY(18px);
            filter: blur(2px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
            filter: blur(0);
          }
        }

        .hero-line {
          opacity: 0;
          animation: heroLineRise 700ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }

        .hero-line-2 {
          animation-delay: 120ms;
        }

        .hero-line-3 {
          animation-delay: 240ms;
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-line {
            opacity: 1;
            animation: none;
            transform: none;
            filter: none;
          }
        }
      `}</style>

      {/* Blueprint Background */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdHRlcm4gaWQ9ImEiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgPjxyZWN0IHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgZmlsbD0iI2Y5ZmFmYiIvPjxwYXRoIGQ9Ik0wIDAgTDYwIDAgTDYwIDYwIEwwIDYwIFoiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2NmYzUwMCIgc3Ryb2tlLXdpZHRoPSIwLjUiIC8+PC9wYXR0ZXJuPjwvc3ZnPg==')] bg-repeat opacity-30"></div>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 px-6 md:px-8 py-16 md:py-24 relative z-10">
        {/* Left Content */}
        <div className="order-1 mt-6 lg:mt-0">
          {/* Badge */}
          <div className="mb-8 -mt-6 inline-flex items-center border border-black px-4 py-2 bg-gray-50 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300">
            <span className="w-2 h-2 bg-amber-500 mr-2 animate-pulse"></span>
            Modern IT Services for Growing Businesses
          </div>

          {/* Main Heading */}
          <h1 className="text-3xl sm:text-[2.4rem] lg:text-[3rem] xl:text-[3.1rem] font-extrabold leading-[1.06] uppercase mb-6 tracking-tight">
            <span className="block hero-line">Architecting The </span>
            <span className="block hero-line hero-line-2 text-amber-600"> Future Of IT</span>
            <span className="block hero-line hero-line-3 text-[0.86em] sm:text-[0.88em]">with Garud Stacks</span>
          </h1>

          {/* Description */}
          <p className="text-gray-600 text-base sm:text-lg mb-10 max-w-xl leading-relaxed">
            Garud Stacks engineers scalable cloud infrastructure, robust cybersecurity, and enterprise software solutions to accelerate your digital transformation.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            {/* Primary Button */}
            <a href="/contact" className="group bg-black text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-amber-600 transition-all duration-300 flex items-center justify-center gap-3 rounded-lg shadow-lg hover:shadow-xl hover:-translate-y-1">
              Schedule a Consultation
              <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
              </svg>
            </a>

            {/* Secondary Button */}
            <button onClick={scrollToWhatWeDo} className="group border-2 border-black text-black px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all duration-300 flex items-center justify-center gap-3 rounded-lg hover:shadow-xl hover:-translate-y-1 cursor-pointer">
              Explore Solutions
              <svg className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
              </svg>
            </button>
          </div>
          
        </div>

        {/* Right Content - Images */}
        <div className="flex items-center justify-center order-2">
          {/* Corporate Image Layout */}
          <div className="relative w-full max-w-lg h-80 sm:h-96 lg:h-[450px] mx-auto mb-10 sm:mb-0">
            {/* Main Large Image */}
            <div className="absolute top-0 right-0 w-[60%] h-[55%] lg:w-[80%] lg:h-[80%] rounded-2xl overflow-hidden shadow-2xl border border-gray-200 z-0 group transition-all duration-300">
              <div className="absolute inset-0 bg-gray-900/10 z-10 group-hover:bg-transparent transition-colors duration-500 pointer-events-none"></div>
              <img 
                src="../img/group1.jpeg" 
                alt="Corporate Meeting" 
                className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-105 cursor-pointer"
                onClick={() => setExpandedImage("../img/group1.jpeg")}
              />
            </div>

            {/* Overlapping Secondary Image */}
            <div className="absolute bottom-0 left-0 w-[60%] h-[55%] rounded-2xl overflow-hidden shadow-2xl border-4 border-white z-10 group transition-all duration-300">
              <img 
                src="../img/large.png" 
                alt="Corporate Team Discussion" 
                className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-105 cursor-pointer"
                onClick={() => setExpandedImage("../img/large.png")}
              />
            </div>

            {/* Floating Trust Badge */}
            <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-max sm:w-auto sm:max-w-none sm:left-auto sm:-translate-x-0 sm:bottom-auto sm:top-1/2 sm:-left-12 sm:-translate-y-1/2 bg-white/95 backdrop-blur-sm px-3 py-2.5 sm:px-5 sm:py-4 rounded-xl shadow-xl border border-gray-100 z-20 flex items-center justify-center sm:justify-start gap-2.5 sm:gap-4 hover:scale-105 hover:shadow-2xl transition-all duration-300">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-gray-900 to-black rounded-full flex items-center justify-center text-amber-500 shadow-inner flex-shrink-0">
                <svg className="w-4 h-4 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
              </div>
              <div>
                <p className="text-[9px] sm:text-[10px] text-gray-500 font-bold uppercase tracking-wider">Enterprise Grade</p>
                <p className="text-[11px] sm:text-sm font-extrabold text-gray-900 leading-tight">Security-First Delivery</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Image Modal */}
      {expandedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setExpandedImage(null)}
        >
          <div 
            className="relative w-full max-w-4xl max-h-[90vh] rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={expandedImage}
              alt="Expanded view"
              className="w-full h-full object-contain"
            />
            <button
              onClick={() => setExpandedImage(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/40 rounded-full flex items-center justify-center transition-colors duration-300 backdrop-blur-sm"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default Hero;
